import {
  BrowserCacheLocation,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';
import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';

/**
 * Single source of truth for auth + backend API configuration.
 *
 * Values are sourced from `src/environments/environment*.ts`, which Angular
 * swaps at build time via `fileReplacements`. There is no runtime override:
 * if a value is missing the app is considered misconfigured and the auth
 * guards / interceptor skip protected flows instead of issuing broken requests.
 */
export const backendApiConfig = {
  endpoint: environment.backendApiEndpoint,
  scope: environment.backendApiScope,
} as const;

const appOrigin = typeof window !== 'undefined' ? window.location.origin : '';

export const authRuntimeConfig = {
  clientId: environment.authClientId,
  tenantId: environment.authTenantId,
  // The /auth route hosts AuthCallback, which processes the AAD
  // callback before any route guards run. This avoids a navigation conflict
  // where MsalGuard triggers a second Angular Router navigation to the login
  // request URL while the guard's own navigation is still in progress.
  redirectUri: appOrigin ? `${appOrigin}/auth` : '',
  postLogoutRedirectUri: appOrigin ? `${appOrigin}/auth` : '',
} as const;

export function isAuthConfigured(): boolean {
  return Boolean(authRuntimeConfig.clientId) && Boolean(authRuntimeConfig.tenantId);
}

export function isBackendApiConfigured(): boolean {
  return Boolean(backendApiConfig.endpoint) && Boolean(backendApiConfig.scope);
}

export function isApplicationConfigured(): boolean {
  return isAuthConfigured() && isBackendApiConfigured();
}

export function isUnauthenticatedTestAccessEnabled(): boolean {
  return !environment.production && environment.allowUnauthenticatedTestAccess;
}

export function msalInstanceFactory(): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: authRuntimeConfig.clientId,
      authority: `https://login.microsoftonline.com/${authRuntimeConfig.tenantId}`,
      redirectUri: authRuntimeConfig.redirectUri,
      postLogoutRedirectUri: authRuntimeConfig.postLogoutRedirectUri,
    },
    cache: {
      // Keep auth session across full PWA process restarts (common on Android).
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
  });
}

export function msalGuardConfigFactory(): MsalGuardConfiguration {
  // MsalGuard performs the interactive redirect automatically when no account
  // is present, then returns the user to the originally requested route. This
  // replaces the previous hand-rolled `acquireTokenSilent` gate, which bounced
  // valid users to the login screen whenever silent iframe renewal was blocked
  // (the common PWA/Safari ITP case).
  const scopes = ['openid', 'profile'];

  if (isBackendApiConfigured()) {
    scopes.push(backendApiConfig.scope);
  }

  return {
    interactionType: InteractionType.Redirect,
    authRequest: { scopes },
    loginFailedRoute: '/login',
  };
}

export function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();

  if (isBackendApiConfigured()) {
    // MSAL Angular v5 uses strict, anchored URL matching by default. A bare
    // endpoint key (e.g. `https://host/api`) only matches that exact URL and
    // does NOT match child paths like `https://host/api/transactions/balance`.
    // Register a wildcard pattern so the interceptor attaches a bearer token
    // to every request issued against the backend API surface.
    const normalizedBase = backendApiConfig.endpoint.replace(/\/+$/, '');
    protectedResourceMap.set(`${normalizedBase}/*`, [backendApiConfig.scope]);
  }

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}
